'use client';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Spacer,
  Text,
} from '@chakra-ui/react';
import { ReactNode, Suspense } from 'react';

import DiscoverySection from '@/app/_components/discovery/DiscoverySection';
import NewVehiclesCard from '@/app/_components/new-vehicles-card/NewVehiclesCard';
import type {
  NewCarData,
  NewCarListing,
  NewCarsLandingPage,
} from '@/types/new-cars/new-cars.types';

import Styles from './NewVehiclesPage.module.scss';

type SimpleGridComponentProps = {
  className?: string;
  children: ReactNode;
  columns?: number[];
  spacingX?: string[];
  spacingY?: string[];
  paddingX?: { [key: string]: string };
};

type BoxComponentProps = {
  className?: string;
  children: ReactNode;
  marginY?: { [key: string]: string };
  paddingX?: { [key: string]: string };
};

const SimpleGridComponent = ({
  className,
  children,
  columns = [1, 1, 2, 3],
  spacingX = ['10px', '30px', '50px', '88px'],
  spacingY = ['50px', '50px', '50px', '88px'],
  paddingX = { md: '6' },
}: SimpleGridComponentProps) => {
  return (
    <SimpleGrid
      className={className}
      columns={columns}
      spacingX={spacingX}
      spacingY={spacingY}
      paddingX={paddingX}
    >
      {children}
    </SimpleGrid>
  );
};

const BoxComponent = ({
  className,
  children,
  marginY = { base: '10', md: '80px' },
  paddingX = { base: '0', sm: '0' },
}: BoxComponentProps) => {
  return (
    <Box className={className} marginY={marginY} paddingX={paddingX}>
      {children}
    </Box>
  );
};

const SpacerComponent = ({ paddingY = { base: 0, md: 4 } }) => {
  return <Spacer paddingY={paddingY} />;
};

type Props = {
  categorizedNewCars: Record<string, NewCarListing[]>;
  newCarsLandingPage: NewCarsLandingPage;
  newCars?: NewCarData[];
};

const NewVehiclesPage = ({
  categorizedNewCars,
  newCarsLandingPage,
  newCars,
}: Props) => {
  return (
    <>
      <Suspense fallback={null}>
        <Container
          maxWidth={{ base: '100%', md: '1328px' }}
          alignContent={'center'}
          paddingX={{ base: 6 }}
          paddingY={{ base: '10', lg: 100 }}
          className={Styles.newVehiclesWrapper}
        >
          <BoxComponent
            className={Styles.headingElement}
            marginY={{ base: '0px', sm: '0px' }}
          >
            <Heading as="h1" className={Styles.titleLg} mb={4}>
              {newCarsLandingPage?.title}
            </Heading>
            <Text className={Styles.titleText}>
              {newCarsLandingPage?.subtitle}
            </Text>
          </BoxComponent>
          {Object.entries(categorizedNewCars ?? {})?.map(([label, values]) => {
            if (!values.length) return;
            return (
              <>
                <BoxComponent className={Styles.headingElement}>
                  <Heading as="h2" className={Styles.titleXl}>
                    {label}
                  </Heading>
                </BoxComponent>
                <SimpleGridComponent className={Styles.vehicleList}>
                  {values?.map((vehicle, index) => (
                    <NewVehiclesCard
                      key={index}
                      newCars={newCars}
                      vehicle={vehicle}
                      priority={index === 0} // Load first image fast
                    />
                  ))}
                </SimpleGridComponent>
                <SpacerComponent />
              </>
            );
          })}
        </Container>
      </Suspense>
      {newCarsLandingPage?.footer_call_to_action_image ? (
        <DiscoverySection
          footer_call_to_action={newCarsLandingPage?.footer_call_to_action}
          footer_call_to_action_image={
            newCarsLandingPage?.footer_call_to_action_image
          }
        />
      ) : null}
    </>
  );
};

export default NewVehiclesPage;
